import Dexie, { type Table } from 'dexie';

export const is_browser = typeof window !== 'undefined';

/**
 * True when IndexedDB exists: browser + vitest (fake-indexeddb) → true,
 * Node SSR → false. Every Dexie construction/access is guarded by this flag
 * so the module graph can be imported server-side without a MissingAPIError.
 */
export const dexie_available =
	typeof indexedDB !== 'undefined' && typeof IDBKeyRange !== 'undefined';

export type AnyRow = { id: string; [key: string]: any };

export type LocalDbConfig = {
	/** Per-app database name, e.g. 'driver', 'seller', 'buyer', 'operator'. */
	name: string;
	/** Dexie `version(1).stores()` map: table name → index string. */
	stores: Record<string, string>;
};

let _instance: Dexie | null = null;

/**
 * Per-app wiring. Each app names its own local DB and declares its own
 * table/index schema. Call once at module scope of the app entry BEFORE any
 * store is created — the lazy `db` proxy below needs a schema to work with.
 * Idempotent: the first call wins, later calls are ignored.
 */
export function init_db(config: LocalDbConfig) {
	if (_instance || !dexie_available) return;
	_instance = new Dexie(config.name);
	_instance.version(1).stores(config.stores);
}

export type LocalDB = Dexie & { [table_name: string]: Table<AnyRow, string> };

/**
 * Lazy singleton: every read resolves against the Dexie instance created by
 * `init_db`. No instance is ever created on the server (dexie_available ===
 * false); every access site guards on `dexie_available` first (see
 * create_store). Touching a table before `init_db` in the browser is a
 * wiring error and surfaces as a descriptive throw, not a silent null.
 */
export const db: LocalDB = new Proxy({} as LocalDB, {
	get(_target, prop) {
		if (!_instance) {
			if (dexie_available) {
				throw new Error(
					'graph-db: init_db() must be called before accessing `db` — wire it at your app entry (the module that imports auth_sdk/graphql).'
				);
			}
			return undefined;
		}
		const value = Reflect.get(_instance, prop);
		return typeof value === 'function' ? value.bind(_instance) : value;
	}
});

const TEST_STORES: Record<string, string> = {
	routes: 'id, date_created, date_updated, is_active, assigned_driver_id',
	shipments: 'id, date_created, date_updated, route_id',
	driver: 'id, date_created, date_updated',
	meta: 'key'
};

/** Test-harness init: gives `db` a schema in vitest regardless of the app
 * entry (which tests often mock away). Call from vitest.setup before any
 * store module is imported. */
export function init_db_for_tests() {
	init_db({ name: 'test', stores: TEST_STORES });
}

export async function reset_db_for_tests() {
	if (!_instance) {
		init_db_for_tests();
	}
	for (const table of _instance!.tables) {
		await table.clear();
	}
}